import { staticPageMetadata } from '../../lib/i18n/staticPageMetadata';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Link, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MetaTags from '../../components/MetaTags';
import ProjectCreationUpgradeBanner from '../../components/billing/ProjectCreationUpgradeBanner';
import ChatModal from '../../components/discovery/Chat';
import HomeHeroRotatingWord from '../../components/home/HomeHeroRotatingWord';
import HomePageBody from '../../components/home/HomePageBody';
import { HOME_FAQ_ITEMS } from '../../components/home/homeFaqContent';
import HOME_ORG_LOGOS from '../../components/home/homeCustomerLogos';
import LogoTicker from '../../components/home/LogoTicker';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interRegular, interSemiBold } from '../../lib/fonts';
import { responsive } from '../../lib/responsive';
import { stripStaticPublicExportPrefix } from '../../lib/i18n/publicWeb';
import { createProjectFromChatDraft, type ChatDigestDraft } from '../../lib/chatProjectCreation';
import { useProjectCreationGate } from '../../lib/hooks/useProjectCreationGate';
import { normalizeOnboardingSeedPaperId } from '../../lib/onboardingProjectIntentRequest';
import { usePublicHomeDigests } from '../../lib/usePublicHomeDigests';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';

export default function PaperzillaLanding() {
    const router = useRouter();
    const { openChat, templateProjectId, seedPaperId, entrySurface } = useLocalSearchParams<{
        openChat?: string;
        templateProjectId?: string;
        seedPaperId?: string;
        entrySurface?: string;
    }>();
    const { fontsLoaded } = useFontLoading();
    const [isChatVisible, setIsChatVisible] = useState(false);
    const hasOpenedFromParam = useRef(false);
    const isNavigatingFromChatRef = useRef(false);
    const { digests, targetCount } = usePublicHomeDigests();
    const projectCreationGate = useProjectCreationGate();

    const normalizedTemplateProjectId = Array.isArray(templateProjectId) ? templateProjectId[0] : templateProjectId;
    const normalizedSeedPaperId = normalizeOnboardingSeedPaperId(seedPaperId);
    const normalizedOpenChat = Array.isArray(openChat) ? openChat[0] : openChat;
    const normalizedEntrySurface = Array.isArray(entrySurface) ? entrySurface[0] : entrySurface;

    const handleChatComplete = async (data: ChatDigestDraft, context?: {
        chatSessionId: string | null;
        seedPaperId: string | null;
    }) => {
        console.log('Chat completed with data:', data);

        isNavigatingFromChatRef.current = true;
        try {
            const result = await createProjectFromChatDraft(data, {
                source: normalizedTemplateProjectId ? 'template' : 'chat',
                chatSessionId: context?.chatSessionId ?? null,
                seedPaperId: context?.seedPaperId ?? null,
            });
            router.replace(result.destination);
        } catch (error) {
            isNavigatingFromChatRef.current = false;
            throw error;
        }
    };

    const handleOpenChat = useCallback(() => {
        projectCreationGate.guardProjectCreation(() => setIsChatVisible(true));
    }, [projectCreationGate.guardProjectCreation]);

    const setPrehydrateChatLoading = useCallback((isLoading: boolean) => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') {
            return;
        }

        const setChatLoading = (window as any).__PZ_SET_CHAT_LOADING;
        if (typeof setChatLoading === 'function') {
            setChatLoading(isLoading);
            return;
        }

        const root = document.documentElement;
        if (!root) return;

        if (isLoading) {
            root.setAttribute('data-chat-loading', '1');
            return;
        }

        root.removeAttribute('data-chat-loading');
    }, []);

    const handleChatClose = () => {
        setIsChatVisible(false);
        setPrehydrateChatLoading(false);
        hasOpenedFromParam.current = false;

        if (isNavigatingFromChatRef.current) {
            return;
        }

        if (Platform.OS === 'web' && typeof window !== 'undefined' && normalizedOpenChat === 'true') {
            const url = new URL(window.location.href);
            url.searchParams.delete('openChat');
            url.searchParams.delete('templateProjectId');
            url.searchParams.delete('seedPaperId');
            url.searchParams.delete('entrySurface');
            const nextRoute = `${stripStaticPublicExportPrefix(url.pathname)}${url.search}` || '/';
            router.replace(nextRoute);
        }
    };

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') {
            return;
        }

        (window as any).__PZ_HYDRATED = true;
        setPrehydrateChatLoading(false);
    }, [setPrehydrateChatLoading]);

    useEffect(() => {
        if (normalizedOpenChat === 'true' && !hasOpenedFromParam.current) {
            hasOpenedFromParam.current = true;
            projectCreationGate.guardProjectCreation(() => setIsChatVisible(true));
            return;
        }

        if (normalizedOpenChat !== 'true') {
            hasOpenedFromParam.current = false;
            setPrehydrateChatLoading(false);
        }
    }, [normalizedOpenChat, projectCreationGate.guardProjectCreation, setPrehydrateChatLoading]);

    useEffect(() => {
        if (isChatVisible) {
            setPrehydrateChatLoading(false);
        }
    }, [isChatVisible, setPrehydrateChatLoading]);

    useEffect(() => {
        if (projectCreationGate.blockedNoticeVisible) {
            setPrehydrateChatLoading(false);
        }
    }, [projectCreationGate.blockedNoticeVisible, setPrehydrateChatLoading]);

    return (
        <>
            <MetaTags
                surfaceId="home"
                faqItems={HOME_FAQ_ITEMS}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header />
                <ScrollView showsVerticalScrollIndicator={false}>
                    <View style={styles.projectCreationBannerShell}>
                        <ProjectCreationUpgradeBanner
                            visible={projectCreationGate.blockedNoticeVisible}
                            onDismiss={projectCreationGate.dismissBlockedNotice}
                            style={styles.projectCreationBanner}
                            snapshot={projectCreationGate.snapshot}
                            message={normalizedSeedPaperId
                                ? msg({ id: 'projectLimit.seedPaperMessage', message: 'Your current plan has reached its digest limit. Upgrade to create a digest for this paper.' })
                                : undefined}
                            testID={normalizedSeedPaperId
                                ? 'paper-seed-project-limit-banner'
                                : undefined}
                        />
                    </View>
                    <LinearGradient
                        colors={['#ffffff', '#fbfcff', '#f6f7fb']}
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={styles.hero}
                        {...responsive('hero-section')}
                    >
                        <View style={styles.container}>
                            <View style={styles.heroSplit} {...responsive('hero-split')}>
                                <View style={styles.heroLeft} {...responsive('hero-left')}>
                                    <View style={styles.heroTitleContainer}>
                                        <View style={styles.heroTitleRow}>
                                            <Trans
                                                id="page.heroRotatingSentence"
                                                comment="Complete home-page hero sentence. The highlighted verb rotates through several choices. Preserve all component tags."
                                            >
                                                <Text
                                                    style={[fonts.displayLarge, styles.heroTitle, fontsLoaded && interSemiBold]}
                                                    {...responsive('hero-title')}
                                                >
                                                    The paper<Text style={styles.heroSuperscript}>*</Text> that{' '}
                                                </Text>
                                                <View
                                                    style={[styles.rotatingWordContainer, styles.rotatingWordPill]}
                                                    {...responsive('hero-rotating-word')}
                                                >
                                                    <HomeHeroRotatingWord fontsLoaded={fontsLoaded} />
                                                </View>
                                                <Text
                                                    style={[fonts.displayLarge, styles.heroTitle, fontsLoaded && interSemiBold]}
                                                    {...responsive('hero-title')}
                                                >
                                                    {' '}your
                                                </Text>
                                                <Text
                                                    style={[fonts.displayLarge, styles.heroTitle, styles.heroTitleContinuation, fontsLoaded && interSemiBold]}
                                                    {...responsive('hero-title')}
                                                >
                                                    research was published this morning.
                                                </Text>
                                            </Trans>
                                        </View>
                                        <View style={[styles.heroQuestionUnderline, { marginTop: 18 }]}>
                                            <Text
                                                style={[fonts.displayLarge, styles.heroQuestion, fontsLoaded && interSemiBold]}
                                                {...responsive('hero-title')}
                                            ><Trans id="page.didYouSeeIt" comment="UI copy in app/(public)/index.tsx">
                                                Did you see it?
                                            </Trans></Text>
                                            <View style={styles.handwrittenUnderline} {...responsive('hero-underline')} />
                                        </View>
                                    </View>
                                </View>

                                <View style={styles.heroRight} {...responsive('hero-right')}>
                                    <Text style={[styles.heroEyebrow, fontsLoaded && interSemiBold]}><Trans id="page.10000NewPapersAppearEveryDay" comment="UI copy in app/(public)/index.tsx">
                                        10,000+ new papers<Text style={styles.heroEyebrowSuperscript}>*</Text> appear every day
                                    </Trans></Text>
                                    <Text
                                        style={[fonts.bodyMedium, styles.heroDescription, fontsLoaded && interRegular]}
                                        {...responsive('hero-description')}
                                    ><Trans id="page.paperzillaTurnsScatteredPaperSourcesIntoAFeedOrAMorningBrief" comment="UI copy in app/(public)/index.tsx">
                                        Paperzilla turns scattered paper sources and forwarded alerts into a relevant, always up-to-date research feed. Read it in your inbox, app, or RSS reader, or let your AI assistant brief you on it every morning.
                                    </Trans></Text>

                                    <View style={styles.heroCtas} {...responsive('hero-ctas')}>
                                        <TouchableOpacity
                                            onPress={handleOpenChat}
                                            style={[styles.heroCtaLink, fontsLoaded && interSemiBold]}
                                            testID="home-open-chat-button"
                                        >
                                            <Text style={[styles.heroCtaText, fontsLoaded && interSemiBold]}><Trans id="page.createYourResearchFeed" comment="UI copy in app/(public)/index.tsx">
                                                Create my research feed
                                            </Trans></Text>
                                        </TouchableOpacity>
                                        <Link
                                            href="/research-briefs"
                                            style={[styles.heroSecondaryLink, fontsLoaded && interSemiBold]}
                                            testID="home-research-briefs-link"
                                            {...responsive('hero-meta')}
                                        ><Trans id="page.seeAMorningBrief" comment="UI copy in app/(public)/index.tsx">
                                            See a morning brief →
                                        </Trans></Link>
                                    </View>

                                    <Text style={[fonts.bodySmall, styles.heroMeta, fontsLoaded && interRegular]} {...responsive('hero-meta')}><Trans id="page.guidedSetupTakesLessThan2Minutes" comment="UI copy in app/(public)/index.tsx">
                                        Describe your topic. We'll guide you through setup.
                                    </Trans></Text>
                                    <Text style={[fonts.bodySmall, styles.heroFootnote, fontsLoaded && interRegular]} {...responsive('hero-meta')}><Trans id="page.papersAndAssociatedSoftwareReposAndDatasets" comment="UI copy in app/(public)/index.tsx">
                                        <Text style={[styles.heroMetaNote, fontsLoaded && interSemiBold]}>*</Text> Papers and associated software repos and datasets.
                                    </Trans></Text>
                                </View>
                            </View>
                        </View>
                    </LinearGradient>

                    <View style={styles.logoContainer}>
                        <LogoTicker logos={HOME_ORG_LOGOS} />
                    </View>

                    <HomePageBody
                        digests={digests}
                        fontsLoaded={fontsLoaded}
                        onOpenChat={handleOpenChat}
                        targetCount={targetCount}
                    />

                    <Footer />
                </ScrollView>
            </View>

            <ChatModal
                visible={isChatVisible}
                onClose={handleChatClose}
                onComplete={handleChatComplete}
                templateProjectId={normalizedTemplateProjectId}
                seedPaperId={normalizedSeedPaperId || undefined}
                entrySurface={normalizedEntrySurface || 'home'}
            />
        </>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    container: {
        alignSelf: 'center',
        maxWidth: 1280,
        paddingHorizontal: 24,
        width: '100%',
    },
    projectCreationBannerShell: {
        alignSelf: 'center',
        maxWidth: 1280,
        paddingHorizontal: 24,
        width: '100%',
    },
    projectCreationBanner: {
        marginTop: 16,
        marginBottom: 0,
    },
    logoContainer: {
        alignSelf: 'center',
        maxWidth: 1280,
        paddingHorizontal: 24,
        width: '100%',
    },
    hero: {
        paddingBottom: 88,
        paddingTop: 72,
    },
    heroSplit: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 56,
        justifyContent: 'space-between',
    },
    heroLeft: {
        alignItems: 'center',
        flex: 1,
        justifyContent: 'center',
    },
    heroRight: {
        borderLeftColor: '#e0e2e7',
        borderLeftWidth: 1,
        flex: 1,
        maxWidth: 500,
        paddingLeft: 56,
        paddingVertical: 16,
    },
    heroTitle: {
        color: colors.text900,
        fontWeight: '600',
        textAlign: 'center',
    },
    heroSuperscript: {
        color: colors.primaryGreenDark,
        fontSize: 14,
        lineHeight: 14,
        marginLeft: 3,
        position: 'relative',
        top: -8,
    },
    heroTitleContainer: {
        alignItems: 'center',
        gap: 8,
    },
    heroTitleRow: {
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    heroTitleContinuation: {
        flexBasis: '100%',
    },
    rotatingWordContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
    },
    rotatingWordPill: {
        backgroundColor: colors.primary100,
        borderRadius: 6,
        marginHorizontal: 3,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    heroQuestionUnderline: {
        position: 'relative',
    },
    handwrittenUnderline: {
        backgroundColor: '#c53030',
        borderRadius: 2,
        bottom: 4,
        height: 4,
        left: '5%',
        position: 'absolute',
        right: '5%',
        transform: [{ rotate: '0.5deg' }, { skewX: '-2deg' }],
    },
    heroQuestion: {
        color: colors.text900,
        fontWeight: '600',
        textAlign: 'center',
    },
    heroEyebrow: {
        color: colors.primaryGreen,
        fontSize: 13,
        fontWeight: '700',
        lineHeight: 18,
        marginBottom: 14,
        textTransform: 'uppercase',
    },
    heroEyebrowSuperscript: {
        fontSize: 9,
        lineHeight: 10,
        position: 'relative',
        top: -3,
    },
    heroDescription: {
        color: colors.text600,
        fontSize: 16,
        lineHeight: 27,
        marginBottom: 28,
    },
    heroMeta: {
        color: colors.text500,
        fontSize: 13,
        letterSpacing: 0.1,
        lineHeight: 20,
        marginTop: 20,
    },
    heroFootnote: {
        color: colors.text500,
        fontSize: 12,
        letterSpacing: 0.1,
        lineHeight: 18,
        marginBottom: 24,
        marginTop: 4,
    },
    heroMetaNote: {
        color: colors.primaryGreenDark,
        fontWeight: '700',
    },
    heroCtas: {
        alignItems: 'center',
        columnGap: 20,
        flexDirection: 'row',
        flexWrap: 'wrap',
        rowGap: 12,
    },
    heroSecondaryLink: {
        color: colors.primaryGreenDark,
        fontSize: 16,
        fontWeight: '600',
        textDecorationLine: 'underline',
    },
    heroCtaLink: {
        backgroundColor: colors.zillaGreen,
        borderRadius: 12,
        paddingHorizontal: 18,
        paddingVertical: 12,
        alignItems: 'center',
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.12,
            shadowRadius: 8,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
            cursor: 'pointer',
        }),
    },
    heroCtaText: {
        color: '#ffffff',
        fontSize: 16,
        fontWeight: '600',
        textAlign: 'center',
    },
});

export function generateMetadata() {
    return staticPageMetadata({ surfaceId: 'home' });
}
